import {
  updateComplianceItem,
  updateCustomComplianceItem,
} from '@commenda/api';
import { createComplianceItem } from '@commenda/api/compliance/createComplianceItem';
import {
  Button,
  DatePicker,
  FieldWrapper,
  Input,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetTitle,
} from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_SOURCE,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { TComplianceItem } from '@commenda/types/src/compliance-calendar/compliances/types';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import {
  ObligationRulesCategory,
  ObligationRulesFrequency,
} from '@prisma/client';
import { useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import {
  ComplianceCategoryEnumToText,
  ComplianceFrequencyEnumToText,
  OrderedComplianceCategories,
} from './types';

const frequencyOptions = [
  ObligationRulesFrequency.ONE_TIME,
  ObligationRulesFrequency.MONTHLY,
  ObligationRulesFrequency.BI_MONTHLY,
  ObligationRulesFrequency.QUARTERLY,
  ObligationRulesFrequency.ANNUAL,
  ObligationRulesFrequency.BIENNIAL,
];

interface CreateFilingSheetProps {
  isOpen: boolean;
  onClose: () => void;
  corporationId?: number;
  companyId?: number;
  loggedInUserId: string;
  editingFiling?: TComplianceItem;
  refreshCompliances: () => void;
  incorporationDate?: string;
  // Called after a successful save. When omitted, falls back to onClose. Lets the
  // parent close the whole Add filing flow on success while Cancel only dismisses the sheet.
  onSuccess?: () => void;
  // When provided (create flow), submitting hands the captured filing back to the parent form
  // instead of persisting here — the parent prefills its steps and adds it via "Add filing".
  onFilingDefined?: (filing: {
    filingName: string;
    category: ObligationRulesCategory;
    frequency: ObligationRulesFrequency;
    firstDueDate: string;
  }) => void;
}

export const CreateFilingSheet = ({
  isOpen,
  onClose,
  corporationId,
  companyId,
  loggedInUserId,
  editingFiling,
  refreshCompliances,
  incorporationDate,
  onSuccess,
  onFilingDefined,
}: CreateFilingSheetProps) => {
  const isEditMode = !!editingFiling;
  const { setNotification } = useNotification();

  const [filingName, setFilingName] = useState(
    editingFiling?.explicitTitle || editingFiling?.title || ''
  );
  const [category, setCategory] = useState<ObligationRulesCategory | ''>(
    editingFiling?.customCategory
      ? (editingFiling.customCategory as ObligationRulesCategory)
      : editingFiling?.category || ''
  );
  const [frequency, setFrequency] = useState<ObligationRulesFrequency | ''>(
    editingFiling?.frequency || ''
  );
  const [firstDueDate, setFirstDueDate] = useState(
    editingFiling?.dueDate || ''
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = !!(
    filingName.trim() &&
    category &&
    frequency &&
    firstDueDate
  );

  const handleSubmit = async () => {
    if (!canSubmit || (!isEditMode && !corporationId)) return;

    // Create flow: don't persist here. Hand the filing back so the form prefills its
    // steps and the user adds it via "Add filing".
    if (onFilingDefined && !isEditMode) {
      onFilingDefined({
        filingName: filingName.trim(),
        category: category as ObligationRulesCategory,
        frequency: frequency as ObligationRulesFrequency,
        firstDueDate,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const customFields = {
        explicitTitle: filingName.trim(),
        customFrequency: frequency as ObligationRulesFrequency,
        customCategory: category as ObligationRulesCategory,
      };

      if (isEditMode && editingFiling) {
        await updateCustomComplianceItem(editingFiling.id, {
          dueDate: firstDueDate,
          isCustom: true,
          customFields,
        });
      } else {
        const res = await createComplianceItem({
          corporationId: corporationId as number,
          dueDate: firstDueDate,
          fiscalYear: new Date(firstDueDate).getFullYear(),
          isCustom: true,
          customFields,
        });

        if (!res.ok) {
          setNotification({
            show: true,
            title: 'Error',
            message: 'Failed to create filing. Please try again.',
            valid: false,
          });
          return;
        }

        const { compliance } = (await res.json()) as {
          compliance: { id: string };
        };
        if (compliance?.id) {
          try {
            await updateComplianceItem(compliance.id, {
              assigneeIdsToAdd: [loggedInUserId],
            });
          } catch (error) {
            console.error('Failed to add default assignee:', error);
          }
        }
      }

      mixpanel.track('CALENDAR_CUSTOM_FILING_ADDED', {
        source: MIXPANEL_SOURCE.CALENDAR,
        surface_area: MIXPANEL_SURFACE_AREA.COMPLIANCE_CALENDAR,
        company_id: companyId,
        is_edit: isEditMode,
      });

      setNotification({
        show: true,
        title: 'Success',
        message: isEditMode
          ? 'Filing updated successfully!'
          : 'Filing created successfully!',
        valid: true,
      });
      refreshCompliances();
      (onSuccess ?? onClose)();
    } catch (error) {
      setNotification({
        show: true,
        title: 'Error',
        message: 'Failed to save filing. Please try again.',
        valid: false,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent side="bottom" preventCloseOnOutsideClick>
        <div className="flex-1 min-h-0 w-full overflow-y-auto overscroll-contain flex flex-col items-center px-10 pt-8 pb-12">
          <div className="w-full max-w-xl flex flex-col gap-10">
            <div className="flex flex-col gap-8">
              <SheetTitle
                className="w-full"
                style={{ fontSize: 24, lineHeight: '32px' }}
              >
                {isEditMode ? 'Edit filing' : 'Create new filing'}
              </SheetTitle>

              <div className="w-full bg-bg-primary border border-border-secondary rounded-lg">
                <div className="flex flex-col gap-2xl p-6">
                  <FieldWrapper>
                    <Label htmlFor="filingName" required>
                      Filing
                    </Label>
                    <Input
                      id="filingName"
                      placeholder="Filing name"
                      value={filingName}
                      onChange={(e) => setFilingName(e.target.value)}
                    />
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label required>Filing type</Label>
                    <Select
                      value={category}
                      onValueChange={(value) =>
                        setCategory(value as ObligationRulesCategory)
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select the filing type" />
                      </SelectTrigger>
                      <SelectContent>
                        {OrderedComplianceCategories.map((option) => (
                          <SelectItem key={option} value={option}>
                            {ComplianceCategoryEnumToText[option] ?? option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="txt-extra-small-regular text-text-tertiary">
                      The category this filing falls under.
                    </p>
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label required>Filing frequency</Label>
                    <Select
                      value={frequency}
                      onValueChange={(value) =>
                        setFrequency(value as ObligationRulesFrequency)
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        {frequencyOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {ComplianceFrequencyEnumToText[option]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="txt-extra-small-regular text-text-tertiary">
                      How often this entity needs to file this.
                    </p>
                  </FieldWrapper>

                  <FieldWrapper>
                    <Label required>First due date</Label>
                    <DatePicker
                      date={firstDueDate}
                      setDate={(date) => setFirstDueDate(date ?? '')}
                      _triggerClassName="border border-border-primary rounded-lg"
                      disabled={
                        incorporationDate
                          ? {
                              before:
                                NonTimezoneSensitiveDate.fromISO8601String(
                                  incorporationDate
                                ).toDate(),
                            }
                          : undefined
                      }
                    />
                    <p className="txt-extra-small-regular text-text-tertiary">
                      We&apos;ll calculate all future due dates from this, based
                      on the frequency.
                    </p>
                  </FieldWrapper>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-4">
              <Button
                variant="secondary"
                size="md"
                type="button"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <LoadingIndicator size={24} />
                    <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                  </>
                ) : isEditMode ? (
                  'Update filing'
                ) : (
                  'Create filing'
                )}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
